import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  GoogleMap,
  LoadScriptNext,
  Marker,
  Polyline,
} from "@react-google-maps/api";
import {
  isSameCoords,
  pointsToLatLngArray,
} from "../utils/tripTracking";

const PRIMARY_STROKE = "rgba(39, 174, 96, 1)";
const PLANNED_ROUTE_COLOR = "#2196F3";

const Map = ({
  path = [],
  driverTrail = [],
  driverLocation = null,
  isLiveTrip = false,
  isCompletedTrip = false,
  startPoint = null,
  endPoint = null,
  passengerMarkers = [],
}) => {
  const mapRef = useRef(null);
  const [boundsInitialized, setBoundsInitialized] = useState(false);
  const [hasMapLoaded, setHasMapLoaded] = useState(false);

  const defaultCenter = useMemo(() => ({ lat: 33.5138, lng: 36.2765 }), []);
  const defaultZoom = 12;

  const sanitizePath = useCallback((rawPoints) => {
    if (!Array.isArray(rawPoints)) return [];

    return rawPoints
      .map((point) => {
        const lat = Number.parseFloat(point?.lat ?? point?.latitude);
        const lng = Number.parseFloat(point?.lng ?? point?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng };
      })
      .filter(Boolean);
  }, []);

  const plannedPathCoordinates = useMemo(
    () => sanitizePath(path),
    [path, sanitizePath],
  );

  const showDriverTrail = Array.isArray(driverTrail) && driverTrail.length > 0;

  const driverTrailCoordinates = useMemo(() => {
    if (!showDriverTrail) return [];
    return pointsToLatLngArray(driverTrail);
  }, [driverTrail, showDriverTrail]);

  const driverMarkerPosition = useMemo(() => {
    if (driverLocation?.lat != null && driverLocation?.lng != null) {
      const lat = Number.parseFloat(driverLocation.lat);
      const lng = Number.parseFloat(driverLocation.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return {
        lat,
        lng,
        heading: Number.parseFloat(driverLocation.heading ?? driverLocation.bearing),
      };
    }

    if (isLiveTrip && driverTrailCoordinates.length > 0) {
      return driverTrailCoordinates[driverTrailCoordinates.length - 1];
    }

    return null;
  }, [driverLocation, driverTrailCoordinates, isLiveTrip]);

  const driverHeading = useMemo(() => {
    const h = Number.parseFloat(driverMarkerPosition?.heading);
    return Number.isFinite(h) ? h : 0;
  }, [driverMarkerPosition]);

  const startMarker = useMemo(() => {
    const lat = Number.parseFloat(
      startPoint?.lat ?? startPoint?.latitude ?? startPoint?.location?.lat,
    );
    const lng = Number.parseFloat(
      startPoint?.lng ?? startPoint?.longitude ?? startPoint?.location?.lng,
    );
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }, [startPoint]);

  const endMarker = useMemo(() => {
    const lat = Number.parseFloat(
      endPoint?.lat ?? endPoint?.latitude ?? endPoint?.location?.lat,
    );
    const lng = Number.parseFloat(
      endPoint?.lng ?? endPoint?.longitude ?? endPoint?.location?.lng,
    );
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }, [endPoint]);

  const handleMapLoad = useCallback((map) => {
    if (mapRef.current) return;
    mapRef.current = map;
    setHasMapLoaded(true);
  }, []);

  useEffect(() => {
    setBoundsInitialized(false);
  }, [
    plannedPathCoordinates.length,
    driverTrailCoordinates.length,
    startMarker?.lat,
    startMarker?.lng,
    endMarker?.lat,
    endMarker?.lng,
    driverMarkerPosition?.lat,
    driverMarkerPosition?.lng,
  ]);

  useEffect(() => {
    if (!hasMapLoaded || boundsInitialized || !mapRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    let count = 0;

    const extend = (p) => {
      if (p?.lat != null && p?.lng != null) {
        bounds.extend(p);
        count += 1;
      }
    };

    plannedPathCoordinates.forEach(extend);
    driverTrailCoordinates.forEach(extend);
    passengerMarkers.forEach(extend);
    if (startMarker) extend(startMarker);
    if (endMarker) extend(endMarker);
    if (driverMarkerPosition) extend(driverMarkerPosition);

    if (count === 0) return;

    if (count === 1) {
      mapRef.current.setCenter(bounds.getCenter());
      mapRef.current.setZoom(14);
    } else {
      mapRef.current.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50,
      });
    }

    setBoundsInitialized(true);
  }, [
    hasMapLoaded,
    boundsInitialized,
    plannedPathCoordinates,
    driverTrailCoordinates,
    passengerMarkers,
    startMarker,
    endMarker,
    driverMarkerPosition,
  ]);

  const isDriverAtStartOrEnd =
    driverMarkerPosition &&
    (isSameCoords(driverMarkerPosition, startMarker) ||
      isSameCoords(driverMarkerPosition, endMarker));

  return (
    <div
      className="isolate overflow-hidden"
      style={{ height: "100%", width: "100%", position: "relative" }}
    >
      <LoadScriptNext googleMapsApiKey={import.meta.env.VITE_GOOGLE_MAP_KEY}>
        <GoogleMap
          mapContainerStyle={{ height: "100%", width: "100%" }}
          center={defaultCenter}
          zoom={defaultZoom}
          options={{
            fullscreenControl: true,
            gestureHandling: "cooperative",
          }}
          onLoad={handleMapLoad}
        >
          {plannedPathCoordinates.length > 0 && (
            <Polyline
              path={plannedPathCoordinates}
              options={{
                strokeColor: PLANNED_ROUTE_COLOR,
                strokeOpacity: 0.9,
                strokeWeight: 3,
                icons: [
                  {
                    icon: {
                      path: "M 0,-1 0,1",
                      strokeOpacity: 1,
                      scale: 3,
                    },
                    offset: "0",
                    repeat: "16px",
                  },
                ],
                zIndex: 1,
              }}
            />
          )}

          {driverTrailCoordinates.length > 1 && (
            <Polyline
              path={driverTrailCoordinates}
              options={{
                strokeColor: PRIMARY_STROKE,
                strokeOpacity: 1,
                strokeWeight: 6,
                geodesic: true,
                zIndex: 3,
              }}
            />
          )}

          {startMarker && (
            <Marker
              position={startMarker}
              title="Trip start"
              label={{ text: "S", color: "#fff", fontWeight: "bold" }}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE,
                fillColor: "#22c55e",
                fillOpacity: 1,
                strokeColor: "#fff",
                strokeWeight: 2,
                scale: 11,
              }}
              zIndex={10}
            />
          )}

          {endMarker && (
            <Marker
              position={endMarker}
              title="Trip end"
              label={{ text: "E", color: "#fff", fontWeight: "bold" }}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE,
                fillColor: "#ef4444",
                fillOpacity: 1,
                strokeColor: "#fff",
                strokeWeight: 2,
                scale: 11,
              }}
              zIndex={10}
            />
          )}

          {passengerMarkers.map((marker) => (
            <Marker
              key={marker.id}
              position={{ lat: marker.lat, lng: marker.lng }}
              title={marker.title}
              label={{
                text: marker.label,
                color: "#fff",
                fontWeight: "bold",
                fontSize: "12px",
              }}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE,
                fillColor: marker.type === "dropoff" ? "#d97706" : "#f97316",
                fillOpacity: 1,
                strokeColor: "#fff",
                strokeWeight: 2,
                scale: 10,
              }}
              zIndex={8}
            />
          ))}

          {driverMarkerPosition &&
            (isLiveTrip || driverLocation) &&
            !isDriverAtStartOrEnd && (
              <Marker
                position={driverMarkerPosition}
                title="Driver"
                icon={{
                  path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW,
                  fillColor: "#2563eb",
                  fillOpacity: 1,
                  strokeColor: "#fff",
                  strokeWeight: 2,
                  scale: 6,
                  rotation: driverHeading,
                }}
                zIndex={100}
              />
            )}
        </GoogleMap>
      </LoadScriptNext>
    </div>
  );
};

export default Map;
